import { DownOutlined, UpOutlined } from '@ant-design/icons';
import { Spin } from 'antd';
import type { MouseEventHandler, ReactNode } from 'react';
import { useState } from 'react';
import styled from 'styled-components';
import { AvatarSizes, renderAvatar } from '../avatars/renderAvatar';
import type { AppSidebarSize } from './AppSidePanel';

const abbreviateName = (name: string): string => {
  if (!name) return '';
  const words = name.trim().split(/\s+/);
  if (words.length <= 1) return name;
  return words.map((word) => word.charAt(0).toUpperCase() + '.').join(' ');
};

const UserMenuWrapper = styled.div<{ $isNarrow?: boolean }>`
  position: relative;
  margin-top: 10px;
  margin-bottom: 10px;
  margin-inline: ${({ $isNarrow }) => ($isNarrow ? '0' : 'auto')};
  width: 100%;
  box-sizing: border-box;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 30px;
  text-align: start;
  display: flex;
  flex-direction: column;
  align-items: ${({ $isNarrow }) => ($isNarrow ? 'center' : 'stretch')};
  overflow: visible;

  .user-menu-button,
  .user-menu-button-small {
    height: 60px;
    border-radius: 10px;
    opacity: 1;
    appearance: none;
    -webkit-appearance: none;
    background: var(--color-surface-raised, #ffffff);
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    padding: 0 10px;
    border: 1px solid transparent;
    cursor: pointer;
    width: 100%;
    font: inherit;
    color: inherit;
    text-align: start;
    &:hover {
      background: var(--color-surface-raised, #ffffff);
      border: 1px solid var(--color-primary, #41aaaf);
    }
    &:focus-visible {
      outline: 2px solid var(--color-primary, #41aaaf);
      outline-offset: 2px;
    }
  }

  .user-menu-button-small {
    background: none;
    flex-direction: column;
    padding: 0;
    width: auto;
    height: auto;
    min-height: 0;
    &:hover {
      background: none;
      border: 1px solid transparent;
      .user-picture {
        border: 1px solid var(--color-primary, #41aaaf);
      }
    }
  }

  .user-menu-button {
    min-height: 65px;
  }

  .user-picture {
    width: 35px;
    height: 35px;
    border-radius: 50px;
    border: 1px solid transparent;
  }

  .user-button-caret {
    margin-top: 3px;
  }

  .user-menu-items {
    position: absolute;
    z-index: 1200;
    bottom: 100%;
    inset-inline-start: 0;
    width: 220px;
    border-radius: 10px;
    background: var(--color-surface-raised, #ffffff);
    margin-bottom: 10px;
    padding: 8px 0 12px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    box-shadow: 10px 10px 10px 0px #0000000d;
    overflow: visible;
  }

  .user-menu-items--from-narrow {
    inset-inline-start: 8px;
  }

  .action-box {
    margin: 8px 16px 0;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    cursor: pointer;
    color: var(--color-text-dark, #042a2a);
    min-height: 28px;
    gap: 12px;
    text-align: start;
  }

  .action-box-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    color: var(--color-text-dark, #042a2a);

    > svg,
    > .anticon {
      width: 20px !important;
      height: 20px !important;
      font-size: 18px !important;
      margin: 0 !important;
      display: block;
      line-height: 1;
    }

    > svg,
    > svg path,
    > .anticon,
    > .anticon svg,
    > .anticon svg path {
      color: currentColor !important;
    }

    > svg path[fill]:not([fill='none']),
    > .anticon svg path[fill]:not([fill='none']) {
      fill: currentColor !important;
    }

    > svg path[stroke]:not([stroke='none']),
    > .anticon svg path[stroke]:not([stroke='none']) {
      stroke: currentColor !important;
    }
  }

  .action-box-label {
    flex: 1;
    min-width: 0;
    text-align: start;
    white-space: nowrap;
    line-height: 20px;
    unicode-bidi: isolate;
  }

  .user-menu-language.action-box {
    margin-inline-end: 12px;
    gap: 0;
  }

  .long-name-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  }

  .name-label-box {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    width: 100%;
    text-align: start;
  }

  .short-name-container {
    display: flex;
    align-items: center;
    gap: 5px;
    justify-content: center;
    width: 100%;
  }
`;

export type AppUserMenuActionProps = {
  icon?: ReactNode;
  label?: ReactNode;
  onClick?: MouseEventHandler<HTMLDivElement>;
  disabled?: boolean;
  extra?: ReactNode;
  className?: string;
  children?: ReactNode;
};

export const AppUserMenuAction = ({
  icon,
  label,
  onClick,
  disabled,
  extra,
  className,
  children,
}: AppUserMenuActionProps) => (
  <div
    className={`action-box${className ? ` ${className}` : ''}`}
    onClick={disabled ? undefined : onClick}
    style={{ cursor: disabled ? 'default' : 'pointer' }}
    role="menuitem"
  >
    {children ?? (
      <>
        {icon ? <span className="action-box-icon">{icon}</span> : null}
        {label ? <span className="action-box-label">{label}</span> : null}
        {extra}
      </>
    )}
  </div>
);

export type AppUserAvatarProps = {
  firstName?: string;
  lastName?: string;
  src?: string;
  size?: number;
};

export const AppUserAvatar = ({
  firstName = '',
  lastName = '',
  src,
  size = 32,
}: AppUserAvatarProps) => {
  const avatarSize =
    size <= 24
      ? AvatarSizes.SMALL
      : size >= 40
        ? AvatarSizes.LARGE
        : AvatarSizes.DEFAULT;

  return (
    renderAvatar(
      firstName || lastName || '?',
      firstName ? lastName : '',
      avatarSize,
      undefined,
      src,
      undefined,
      true,
    ) ?? null
  );
};

export type AppUserMenuClose = () => void;

export type AppUserMenuProps = {
  size: AppSidebarSize;
  loading?: boolean;
  displayName: string;
  ariaLabel?: string;
  avatar?: ReactNode;
  /** Subscription / status tag under the name (wide) or avatar (narrow). */
  subtitle?: ReactNode;
  /** Trial countdown or other content under the account chip.
   * Pass `null` to omit the default sidebar spacer. */
  belowButton?: ReactNode;
  /** Keep the dropdown open (e.g. nested language picker). */
  holdOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  children?: ReactNode | ((api: { close: AppUserMenuClose }) => ReactNode);
};

export const AppUserMenu = ({
  size,
  loading = false,
  displayName,
  ariaLabel,
  avatar,
  subtitle,
  belowButton,
  holdOpen = false,
  onOpenChange,
  children,
}: AppUserMenuProps) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const isNarrow = size === 'narrow';
  const abbreviated = abbreviateName(displayName);
  const longUserName = displayName.length > 30;
  const userButtonWidth = isNarrow
    ? { width: '100%', maxWidth: '100%' }
    : { width: 173, maxWidth: '100%', alignSelf: 'center' as const };

  const setOpen = (open: boolean) => {
    setMenuOpen(open);
    onOpenChange?.(open);
  };
  const closeMenu = () => setOpen(false);
  const items =
    typeof children === 'function' ? children({ close: closeMenu }) : children;

  return (
    <Spin spinning={loading}>
      <UserMenuWrapper style={userButtonWidth} $isNarrow={isNarrow}>
        {menuOpen && (
          <div
            className={`user-menu-items${
              isNarrow ? ' user-menu-items--from-narrow' : ''
            }`}
            role="menu"
            onMouseLeave={() => {
              if (!holdOpen) closeMenu();
            }}
          >
            {items}
          </div>
        )}

        <button
          type="button"
          className={isNarrow ? 'user-menu-button-small' : 'user-menu-button'}
          onClick={() => setOpen(!menuOpen)}
          aria-expanded={menuOpen}
          aria-haspopup="menu"
          aria-label={ariaLabel || displayName || 'Account'}
        >
          <div
            className={
              longUserName ? 'long-name-container' : 'short-name-container'
            }
          >
            {avatar ? <div style={{ marginTop: 4 }}>{avatar}</div> : null}
            {!isNarrow && (
              <div className="name-label-box">
                <span style={{ textAlign: 'start', lineHeight: '18px' }}>
                  {abbreviated}
                </span>
                {subtitle}
              </div>
            )}
          </div>
          {!isNarrow && (
            <div className="user-button-caret">
              {menuOpen ? <UpOutlined /> : <DownOutlined />}
            </div>
          )}
          {isNarrow && subtitle}
        </button>

        {belowButton !== undefined ? (
          belowButton
        ) : (
          <div style={{ height: 35 }} aria-hidden />
        )}
      </UserMenuWrapper>
    </Spin>
  );
};
